import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Check, Loader2, MessageCircle, FileSearch, KeyRound, AlertCircle, Inbox,
  ClipboardList, FileCheck, Landmark, PartyPopper,
} from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";
import { FlagDot, usePageTitle } from "../components/Shared";
import { WHATSAPP_NUMBER } from "../data/content";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMELINE_ICONS = [Inbox, ClipboardList, FileCheck, Landmark, PartyPopper];
const TIMELINE_KEYS = ["recue", "en_etude", "documents_recus", "en_traitement", "terminee"];

const TIMELINE_TEXT = {
  fr: [
    { label: "Demande reçue", desc: "Votre demande est bien enregistrée. Un conseiller DOC TOGO va l'étudier sous 24h ouvrées." },
    { label: "Étude & devis en cours", desc: "Nous analysons votre dossier et préparons votre devis détaillé ainsi que la liste des pièces requises." },
    { label: "Documents vérifiés", desc: "Vos pièces sont complètes et conformes. Votre dossier va être déposé auprès de l'administration." },
    { label: "Traitement auprès de l'administration", desc: "Votre dossier est déposé et en cours de traitement par l'administration compétente." },
    { label: "Démarche terminée", desc: "Votre document ou le résultat de votre démarche est prêt. Nous organisons la remise avec vous." },
  ],
  en: [
    { label: "Request received", desc: "Your request has been registered. A DOC TOGO advisor will review it within 24 business hours." },
    { label: "Review & quote in progress", desc: "We are analysing your case and preparing your detailed quote and the list of required documents." },
    { label: "Documents verified", desc: "Your documents are complete and compliant. Your file is about to be filed with the authorities." },
    { label: "Processing with the authorities", desc: "Your file has been filed and is being processed by the competent administration." },
    { label: "Procedure completed", desc: "Your document or result is ready. We are arranging delivery with you." },
  ],
};

const COPY = {
  fr: {
    meta: "Suivi de dossier",
    eyebrow: "Suivi de dossier",
    h1a: "Où en est",
    h1b: "votre démarche ?",
    sub: "Entrez la référence reçue lors de votre demande (format DT-AAAAAA-XXXX) pour suivre l'avancement de votre dossier en temps réel.",
    placeholder: "Ex : DT-202609-70C0",
    search: "Rechercher",
    notFoundTitle: "Aucun dossier trouvé",
    notFoundText: "Vérifiez votre référence (format DT-AAAAAA-XXXX, indiquée dans votre email de confirmation). Si le problème persiste, contactez-nous sur WhatsApp.",
    hint: "Votre référence figure dans l'email de confirmation reçu après l'envoi de votre demande, ainsi que sur l'écran de confirmation du formulaire.",
    dossier: "Dossier",
    openedOn: "Ouvert le",
    inProgress: "En cours",
    whatsapp: "Demander un point sur WhatsApp",
    whatsappMsg: (ref) => `Bonjour DOC TOGO, je souhaite un point sur mon dossier ${ref}.`,
    adminToggle: "Espace équipe DOC TOGO",
    errNetwork: "Le service est momentanément indisponible. Réessayez dans un instant ou contactez-nous sur WhatsApp.",
  },
  en: {
    meta: "Case tracking",
    eyebrow: "Case tracking",
    h1a: "Where does",
    h1b: "your case stand?",
    sub: "Enter the reference received when you submitted your request (format DT-YYYYMM-XXXX) to track your case in real time.",
    placeholder: "E.g. DT-202609-70C0",
    search: "Search",
    notFoundTitle: "No case found",
    notFoundText: "Check your reference (format DT-YYYYMM-XXXX, shown in your confirmation email). If the problem persists, contact us on WhatsApp.",
    hint: "Your reference appears in the confirmation email received after submitting your request, and on the form's confirmation screen.",
    dossier: "Case",
    openedOn: "Opened on",
    inProgress: "In progress",
    whatsapp: "Request an update on WhatsApp",
    whatsappMsg: (ref) => `Hello DOC TOGO, I would like an update on my case ${ref}.`,
    adminToggle: "DOC TOGO team area",
    errNetwork: "The service is temporarily unavailable. Please try again in a moment or contact us on WhatsApp.",
  },
};

const formatDate = (iso, lang) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "—";
  }
};

const inputCls =
  "w-full rounded-xl border border-brand/15 bg-white px-4 py-3 text-base sm:text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-brand focus:ring-2 focus:ring-brand/15";

export default function Suivi() {
  const { lang } = useLang();
  const c = COPY[lang];
  usePageTitle(c.meta);
  const [searchParams] = useSearchParams();
  const [ref, setRef] = useState(searchParams.get("ref") || "");
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [token, setToken] = useState("");
  const [newStatut, setNewStatut] = useState("en_etude");
  const [updating, setUpdating] = useState(false);

  const timeline = TIMELINE_KEYS.map((key, i) => ({ key, icon: TIMELINE_ICONS[i], ...TIMELINE_TEXT[lang][i] }));

  const lookup = async (value) => {
    const reference = (value ?? ref).trim().toUpperCase();
    if (!reference) return;
    setLoading(true);
    setNotFound(false);
    setResult(null);
    try {
      const res = await axios.get(`${API}/suivi/${encodeURIComponent(reference)}`);
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error(c.errNetwork);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initial = searchParams.get("ref");
    if (initial) lookup(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatut = async () => {
    if (!token || !result) return;
    setUpdating(true);
    try {
      const res = await axios.put(
        `${API}/suivi/${encodeURIComponent(result.reference)}/statut`,
        { statut: newStatut },
        { headers: { "X-Admin-Token": token } }
      );
      setResult((r) => ({ ...r, statut: res.data.statut, updated_at: new Date().toISOString() }));
      toast.success("Statut mis à jour — le client a été notifié par email.");
    } catch (err) {
      toast.error(err.response?.status === 401 ? "Token admin invalide." : "Erreur lors de la mise à jour.");
    } finally {
      setUpdating(false);
    }
  };

  const currentIndex = result ? Math.max(0, TIMELINE_KEYS.indexOf(result.statut)) : -1;

  return (
    <div data-testid="suivi-page">
      <section className="relative overflow-hidden bg-brand-deep text-white">
        <div className="pointer-events-none absolute -top-32 right-[-5%] h-96 w-96 rounded-full bg-gold/15 blur-[110px]" />
        <div className="relative mx-auto max-w-4xl px-6 pt-32 pb-16 lg:pt-40 lg:pb-20">
          <div className="mb-5 flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.3em] text-gold">
            <FlagDot /> {c.eyebrow}
          </div>
          <MaskedLines
            key={lang}
            lines={[<>{c.h1a}</>, <>{c.h1b}</>]}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.06] text-balance"
          />
          <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-white/60">{c.sub}</p>
          <form
            data-testid="suivi-search-form"
            onSubmit={(e) => { e.preventDefault(); lookup(); }}
            className="mt-9 flex flex-col gap-3 sm:flex-row"
          >
            <input
              value={ref}
              onChange={(e) => setRef(e.target.value.toUpperCase())}
              data-testid="suivi-input-ref"
              placeholder={c.placeholder}
              autoCapitalize="characters"
              autoComplete="off"
              enterKeyHint="search"
              className="flex-1 rounded-full border border-white/20 bg-white/5 px-6 py-4 font-mono text-base sm:text-sm font-bold uppercase tracking-widest text-white placeholder:text-white/30 outline-none backdrop-blur-md transition-[border-color,background-color] duration-300 focus:border-gold focus:bg-white/10"
            />
            <button
              type="submit"
              disabled={loading || !ref.trim()}
              data-testid="suivi-search-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-display font-bold text-brand-deep transition-[background-color,transform] duration-300 hover:bg-gold-warm hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
              {c.search}
            </button>
          </form>
        </div>
      </section>

      <section className="bg-ivory py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-6">
          {notFound && (
            <Reveal>
              <div data-testid="suivi-not-found" className="flex items-start gap-4 rounded-3xl border border-crimson/20 bg-red-50 p-7">
                <AlertCircle className="mt-0.5 h-6 w-6 shrink-0 text-crimson" />
                <div>
                  <h3 className="font-display text-lg font-bold text-slate-900">{c.notFoundTitle}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{c.notFoundText}</p>
                </div>
              </div>
            </Reveal>
          )}

          {!result && !notFound && (
            <Reveal>
              <div className="rounded-3xl border border-dashed border-brand/20 bg-white p-10 text-center">
                <FileSearch className="mx-auto h-10 w-10 text-brand/40" />
                <p className="mt-4 text-sm text-slate-500">{c.hint}</p>
              </div>
            </Reveal>
          )}

          {result && (
            <Reveal>
              <div data-testid="suivi-result" className="overflow-hidden rounded-3xl border border-brand/10 bg-white shadow-sm">
                <div className="flex flex-col gap-4 border-b border-brand/10 bg-brand-deep p-7 text-white sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="font-mono text-xs uppercase tracking-[0.25em] text-white/50">{c.dossier}</div>
                    <div data-testid="suivi-result-ref" className="mt-1 font-mono text-2xl font-bold text-gold">{result.reference}</div>
                  </div>
                  <div className="text-sm text-white/60 sm:text-right">
                    <div className="font-semibold text-white">{result.categorie}</div>
                    {result.service && <div>{result.service}</div>}
                    <div className="mt-1 text-xs">{c.openedOn} {formatDate(result.created_at, lang)}</div>
                  </div>
                </div>
                <div className="p-7 sm:p-9">
                  <ol data-testid="suivi-timeline" className="relative space-y-0">
                    {timeline.map((step, i) => {
                      const done = i < currentIndex;
                      const current = i === currentIndex;
                      return (
                        <li key={step.key} data-testid={`suivi-step-${step.key}`} className="relative flex gap-5 pb-9 last:pb-0">
                          {i < timeline.length - 1 && (
                            <span className={`absolute left-[22px] top-12 h-[calc(100%-3rem)] w-0.5 ${done ? "bg-brand" : "bg-slate-200"}`} />
                          )}
                          <span
                            className={`relative z-10 grid h-11 w-11 shrink-0 place-items-center rounded-full border-2 ${
                              done
                                ? "border-brand bg-brand text-white"
                                : current
                                  ? "border-gold bg-gold-glow text-gold animate-pulse-soft"
                                  : "border-slate-200 bg-white text-slate-300"
                            }`}
                          >
                            {done ? <Check className="h-5 w-5" /> : <step.icon className="h-5 w-5" />}
                          </span>
                          <div className="pt-1.5">
                            <h3 className={`font-display text-base font-bold sm:text-lg ${current ? "text-gold" : done ? "text-brand" : "text-slate-400"}`}>
                              {step.label}
                              {current && (
                                <span className="ml-3 rounded-full bg-gold-glow px-3 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-gold">
                                  {c.inProgress}
                                </span>
                              )}
                            </h3>
                            <p className={`mt-1 text-sm leading-relaxed ${current || done ? "text-slate-600" : "text-slate-400"}`}>
                              {step.desc}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                  <div className="mt-9 flex flex-col gap-3 border-t border-slate-100 pt-7 sm:flex-row">
                    <a
                      href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(c.whatsappMsg(result.reference))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="suivi-whatsapp-btn"
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-display text-sm font-bold text-white transition-[background-color,transform] duration-300 hover:bg-[#1fb857] hover:-translate-y-0.5"
                    >
                      <MessageCircle className="h-4 w-4" /> {c.whatsapp}
                    </a>
                    <button
                      onClick={() => setAdminOpen(!adminOpen)}
                      data-testid="suivi-admin-toggle"
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-500 transition-[border-color,color] duration-300 hover:border-brand hover:text-brand"
                    >
                      <KeyRound className="h-4 w-4" /> {c.adminToggle}
                    </button>
                  </div>
                  {adminOpen && (
                    <div data-testid="suivi-admin-panel" className="mt-6 rounded-2xl border border-gold/30 bg-gold-glow/40 p-6">
                      <h4 className="font-display text-sm font-bold text-slate-900">Mettre à jour le statut de ce dossier</h4>
                      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <input
                          type="password"
                          value={token}
                          onChange={(e) => setToken(e.target.value)}
                          data-testid="suivi-admin-token"
                          placeholder="Token admin"
                          autoComplete="off"
                          className={inputCls}
                        />
                        <select
                          value={newStatut}
                          onChange={(e) => setNewStatut(e.target.value)}
                          data-testid="suivi-admin-statut"
                          className={inputCls}
                        >
                          {TIMELINE_TEXT.fr.map((t, i) => <option key={TIMELINE_KEYS[i]} value={TIMELINE_KEYS[i]}>{t.label}</option>)}
                        </select>
                        <button
                          onClick={updateStatut}
                          disabled={updating || !token}
                          data-testid="suivi-admin-update-btn"
                          className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand px-5 py-3 font-display text-sm font-bold text-white transition-[background-color] duration-300 hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                          Mettre à jour
                        </button>
                      </div>
                      <p className="mt-3 text-xs text-slate-500">
                        Le client est automatiquement notifié par email à chaque changement de statut.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
